/**
 * Statistics Block Functionality
 *
 * Handles animated number counting for the statistics block.
 * Numbers animate from 0 to their target value when the block comes into view.
 *
 * @package Karuna_Hospice
 */

/**
 * Animate a number from start to end value
 *
 * @param {HTMLElement} element The element containing the number to animate
 * @param {number} start The starting value
 * @param {number} end The ending value
 * @param {number} duration Duration of animation in milliseconds
 */
function animateNumber( element, start, end, duration ) {
	const startTime = performance.now();
	const difference = end - start;

	function update( currentTime ) {
		const elapsed = currentTime - startTime;
		const progress = Math.min( elapsed / duration, 1 );

		// Easing function for smooth animation (ease-out)
		const easeProgress = 1 - Math.pow( 1 - progress, 3 );

		const currentValue = Math.floor( start + difference * easeProgress );
		element.textContent = currentValue.toLocaleString();

		if ( progress < 1 ) {
			requestAnimationFrame( update );
		} else {
			element.textContent = end.toLocaleString();
		}
	}

	requestAnimationFrame( update );
}

/**
 * Initialize statistics block functionality
 */
export function initStatistics() {
	const statisticsBlocks = document.querySelectorAll( '[data-statistics-block]' );

	if ( ! statisticsBlocks.length ) {
		return;
	}

	// Create Intersection Observer to trigger animation when block comes into view
	const observerOptions = {
		root: null,
		rootMargin: '0px',
		threshold: 0.3, // Trigger when 30% of block is visible
	};

	const observer = new IntersectionObserver( ( entries ) => {
		entries.forEach( ( entry ) => {
			if ( entry.isIntersecting ) {
				const block = entry.target;
				const numberElements = block.querySelectorAll( '[data-statistics-number]' );

				numberElements.forEach( ( element ) => {
					// Check if already animated
					if ( element.dataset.animated === 'true' ) {
						return;
					}

					const targetValue = parseInt( element.dataset.statisticsNumber, 10 );

					if ( ! isNaN( targetValue ) && targetValue > 0 ) {
						// Mark as animated
						element.dataset.animated = 'true';

						// Animate from 0 to target value over 2 seconds
						animateNumber( element, 0, targetValue, 2000 );
					}
				} );

				// Unobserve after animation starts to prevent re-triggering
				observer.unobserve( block );
			}
		} );
	}, observerOptions );

	// Observe all statistics blocks
	statisticsBlocks.forEach( ( block ) => {
		observer.observe( block );
	} );
}

